import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { PrimaryButton } from '@bibliocommons/deprecated-base-buttons';
import Pagination from 'app/components/shared/Pagination';
import { searchQueryShape } from 'app/shapes/search';
import BaseAddTitle, { CatalogueResults } from './components';

import './AddTitle.scss';

export class AddTitle extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      doneButtonContainerWidth: 0,
      isInitialSearch: true,
      results: null
    };
    bindAll(this);

    // Built here rather than in componentDidMount so there is exactly one wrapper per instance, and
    // so all instance wiring sits together. It matters beyond tidiness: under React 18 StrictMode a
    // double-invoked mount would build a second wrapper if this lived in componentDidMount, and
    // addEventListener would then hold two listeners of which only the later is removable. With a
    // single stable reference the DOM de-duplicates the repeat attach. (bindAll has already run, so
    // this.handleResize is bound.)
    this.debouncedHandleResize = debounce(this.handleResize, 100);
  }

  static getDerivedStateFromProps(props, state) {
    const results = props.results;
    if (state.isInitialSearch) {
      const isQueryTheSame = props.searchQuery.get('query') === state.query && results === state.results;
      const hasResultsChanged = results !== state.results;
      return isQueryTheSame || hasResultsChanged ? { isInitialSearch: false, results } : { results };
    }
    return { results };
  }

  componentDidMount() {
    // Attach the debounced wrapper, not handleResize: the wrapper is what gets attached, so it is
    // also what removeEventListener has to be given. Passing the unwrapped method matched nothing
    // -- removeEventListener compares by reference and fails silently -- so every mount left a
    // resize listener on window for the life of the page. See CORE-78822.
    window.addEventListener('resize', this.debouncedHandleResize);
  }

  componentWillUnmount() {
    // Removing the listener does not cancel an invocation lodash has already queued; .cancel()
    // does. Without it a resize within the 100ms window still calls setState after unmount.
    this.debouncedHandleResize.cancel();
    window.removeEventListener('resize', this.debouncedHandleResize);
  }

  handleCatalogueSearch(query) {
    const { searchActions, searchQuery } = this.props;
    const curPage = query === searchQuery.get('query') ? searchQuery.get('page') : 1;
    searchActions.addTitleSearch({
      ...searchQuery.toJS(),
      query,
      page: curPage
    });
    this.setState({ query });
  }

  handleResize() {
    if (this._doneButtonContainer && this._addTitleWrapper) {
      this.setState({
        doneButtonContainerWidth: `${this._addTitleWrapper.offsetWidth}px`
      });
    }
  }

  handlePagination(page) {
    const { searchActions, searchQuery } = this.props;
    searchActions.addTitleSearch({
      ...searchQuery.toJS(),
      page
    });
  }

  handleCatalogueSearchResults() {
    const { zeroResults, results, availabilityDetailActions, addRowClassNames } = this.props;

    if (this.state.isInitialSearch || (zeroResults && !this.state.query)) {
      return null;
    }
    if (zeroResults && this.state.query) {
      return (
        <div className="no-results-found">
          <FormattedMessage id="add_title_no_results_found" />
        </div>
      );
    }

    return (
      <div>
        <div className="pagination-holder">
          {this.renderPagination(true)}
          <CatalogueResults
            className="clearfix"
            searchResults={results}
            availabilityDetailActions={availabilityDetailActions}
            renderAddOn={item => this.props.renderAddOn(item)}
            addRowClassNames={addRowClassNames}
          />
          {this.renderPagination(false, 'pagination-right')}
        </div>
        <div
          className="clearfix done-button-container"
          style={{ width: this.state.doneButtonContainerWidth }}
          ref={el => {
            this._doneButtonContainer = el;
          }}
        >
          {this.renderAlert()}
          <PrimaryButton className="done-button" handleClick={this.props.handleClose}>
            <FormattedMessage id="done" />
          </PrimaryButton>
        </div>
      </div>
    );
  }

  addTitleRef(el) {
    this._addTitleWrapper = el;
  }

  renderAlert() {
    return this.props.renderAlert ? this.props.renderAlert() : null;
  }

  renderPagination(focus, className = '') {
    const { isFetching, pagination } = this.props;
    return (
      <Pagination
        compact
        className={className}
        autoFocus={focus}
        isFetching={isFetching}
        textKey="Search.paginationInfo"
        pagination={pagination}
        handleClick={this.handlePagination}
      />
    );
  }

  render() {
    const { isFetching, isWrappedInForm } = this.props;
    const catalogueSearchOptions = {
      tabName: 'catalogue search',
      searchLabel: <FormattedMessage id="search_by_keyword" />,
      handleSearch: this.handleCatalogueSearch,
      renderResults: this.handleCatalogueSearchResults,
      query: this.state.query
    };

    return (
      <div className="cp-shared-add-title">
        <div className="add-title-wrapper" ref={this.addTitleRef}>
          <BaseAddTitle
            catalogueOptions={catalogueSearchOptions}
            onUpdateHandle={this.handleResize}
            searching={isFetching}
            isWrappedInForm={isWrappedInForm}
          />
        </div>
      </div>
    );
  }
}

AddTitle.propTypes = {
  searchActions: PropTypes.objectOf(PropTypes.func).isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  renderAlert: PropTypes.func,
  searchQuery: searchQueryShape.isRequired,
  pagination: ImmutablePropTypes.map.isRequired,
  zeroResults: PropTypes.bool.isRequired,
  results: ImmutablePropTypes.list.isRequired,
  handleClose: PropTypes.func.isRequired,
  addRowClassNames: PropTypes.func,
  renderAddOn: PropTypes.func,
  isFetching: PropTypes.bool,
  isWrappedInForm: PropTypes.bool
};

AddTitle.defaultProps = {
  renderAddOn: () => {},
  isWrappedInForm: false
};

export default AddTitle;
